import {
    DataStore,
    FeedbackStore,
    BaseStore,
    ScreenSizeStore,
} from '../stores';
import { FILTER_SEARCH_KEYS } from '../constants';
import { action, computed, observable, toJS, isArrayLike } from 'mobx';
import get from 'lodash/get';
import { Stores } from '../models/Stores';
import {
    FilterSearchKeys,
    FilterProps,
    FiltersProps,
} from '../models/components/Filter';
import { ClearIndividualFilters } from '../models/configuration/filter/ClearIndividualFilters';
import { getNonFilterParams } from '../utils';
import { isGUID } from '../utils/isGUID';
import { QueryStringOptions } from '../models/configuration/explorer/QueryStringOptions';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import { utils, helpers, models } from '@kurtosys/ksys-app-template/dist';
import { GlobalStorageConfigSource } from '../models/configuration/global/GlobalStorageConfigSource';
import { GlobalUserFiltersConfig } from '../models/configuration/global/GlobalUserFiltersConfig';
import { isNullOrUndefined } from '@kurtosys/ksys-app-template/dist/utils/typeChecks';
import { FeedbackType } from '../Components/Feedback/Models/FeedbackType';
import { ShareableLink } from '../models/configuration/filter/ShareableLink';
import { FeedbackConfig } from '../models/configuration/FeedbackConfig';

export class FilterStore extends BaseStore {
    dataStore: DataStore;
    feedbackStore: FeedbackStore;
    screenSizeStore: ScreenSizeStore;
    @observable showShareableLinkConfirm: boolean = false;
    isInitialized: boolean = false;
    storageHelper: helpers.StorageHelper;
    storageKey = 'user_filters';
    @observable userFilters: Record<string, Array<string>> = {};
    @observable isFiltersOpen: boolean = false;

    constructor(stores: Stores) {
        super(stores);
        this.dataStore = stores.data;
        this.feedbackStore = stores.feedback;
        this.screenSizeStore = stores.screenSize;
    }

    initialize = async (): Promise<void> => {
        if (!this.isInitialized) {
            const userFilterConfig = this.userFiltersConfig;
            let storageType: models.helpers.StorageHelper.TStorageType;
            if (userFilterConfig) {
                switch (userFilterConfig.source) {
                    case GlobalStorageConfigSource.localStorage:
                        storageType = 'LOCAL';
                        break;
                    case GlobalStorageConfigSource.userConfiguration:
                        storageType = 'DB';
                        break;
                    case GlobalStorageConfigSource.sessionStorage:
                        storageType = 'SESSION';
                        break;
                }
                const storageHelperOptions: models.helpers.StorageHelper.IStorageHelperConfigurationOptions = {
                    type: storageType,
                    obfuscated: userFilterConfig.obfuscateValuesStored,
                    allowMultiple: userFilterConfig.allowMultiple,
                    getUserConfiguration: this.stores.data.services
                        .getUserConfiguration,
                    setUserConfiguration: this.stores.data.services
                        .setUserConfiguration,
                };
                this.storageHelper = new helpers.StorageHelper(
                    storageHelperOptions
                );
                await this.getUserFilters();
            }
            this.isInitialized = true;
        }
    };

    @action
    toggleExpandFilters = () => {
        this.isFiltersOpen = !this.isFiltersOpen;
    };

    @action
    async getUserFilters() {
        if (this.storageHelper) {
            const response = (await this.storageHelper.getStorageValues(
                this.storageKey
            )) as Record<string, Array<string>>;
            if (response) {
                this.userFilters = response;
            }
        } else {
            console.warn(
                'Storage Helper not initialized, please check global user filter configuration'
            );
        }
    }

    @action
    async setUserFilters() {
        if (this.storageHelper) {
            await this.storageHelper.setStorageValues(
                this.storageKey,
                this.userFilters
            );
        } else {
            console.warn(
                'Storage Helper not initialized, please check global user filter configuration'
            );
        }
    }

    @computed
    get userFiltersConfig(): GlobalUserFiltersConfig {
        return (
            this.stores.data &&
            this.stores.data.config &&
            this.stores.data.config.globalUserFiltersConfig
        );
    }

    shouldRenderFilter(filter): boolean {
        const { noDataDisplay = 'show' } = this.dataStore.config.filterOptions;
        const { values } = filter;
        return (
            noDataDisplay === 'show' ||
            (noDataDisplay === 'hide' &&
                !utils.typeChecks.isNullOrEmpty(toJS(values)))
        );
    }

    @computed
    get filters(): Array<FilterProps> {
        const filters = [];
        for (const filter of this.dataStore.config.filtersConfig) {
            const isDocumentFilter = this.dataStore.config.isDocumentFilter(
                filter.property
            );
            const fundTypeEntity = this.dataStore.config.entityType;
            if (isDocumentFilter || filter.type === fundTypeEntity) {
                const orchestratedFilter = {
                    ...filter,
                    values: get(this.dataStore.filters, filter.property, []),
                    filterSearchKey: get(FILTER_SEARCH_KEYS, filter.type, ''),
                };
                if (this.shouldRenderFilter(orchestratedFilter)) {
                    filters.push(orchestratedFilter);
                }
            } else {
                console.warn(
                    `The configured filter type (${filter.type}) does not match the Fund entity type (${fundTypeEntity})`
                );
            }
        }

        return filters;
    }

    @computed
    get filtersByProperty() {
        return this.filters.reduce((config, filter) => {
            if (!config[filter.property]) {
                config[filter.property] = filter;
            }
            return config;
        }, {});
    }

    @computed
    get minimumFiltersNeeded(): Array<string> | undefined {
        const minimumFiltersNeeded =
            this.dataStore &&
            this.dataStore.config &&
            this.dataStore.config.filterOptions &&
            this.dataStore.config.filterOptions.minimumFiltersNeeded;
        return minimumFiltersNeeded;
    }

    isFilterRequired = (property: string) => {
        return (
            isArrayLike(this.minimumFiltersNeeded) &&
            this.minimumFiltersNeeded.includes(property)
        );
    };

    @action
    toggleFilters = () => {
        this.isFiltersOpen = !this.isFiltersOpen;
    };

    @computed
    get filtersCount(): number {
        const uniqueFilters = [];
        const filtersConfigByProperty = this.stores.data.config
            .filtersConfigByProperty;
        return [
            ...this.dataStore.filterSearch,
            ...this.dataStore.documentFilterSearch,
            ...this.dataStore.documentFilterInferredSearch,
            ...this.dataStore.documentPropertySearch,
        ]
            .filter(
                filter => filtersConfigByProperty[filter.property] !== undefined
            )
            .reduce((count, filter) => {
                if (
                    uniqueFilters.find(
                        property => filter.property === property
                    ) === undefined
                ) {
                    uniqueFilters.push(filter.property);
                    count++;
                }
                return count;
            }, 0);
    }

    @computed
    get isFiltersTop(): boolean {
        const { isMobile } = this.screenSizeStore;
        return !isMobile && this.dataStore.config.filtersTop;
    }

    // Config if the top filters should be expandable
    @computed
    get filtersTopExpandable(): boolean {
        return this.dataStore.config.filtersTopExpandable;
    }

    // Config if the filter top should be expanded by default
    @action
    filtersOpenDefault() {
        const { isMobile } = this.screenSizeStore;
        // Mobile should not be expanded and the side filters can not expand
        this.isFiltersOpen =
            (!isMobile &&
                this.dataStore.config.filtersTop &&
                this.dataStore.config.filtersTopExpanded) ||
            false;
    }

    @computed
    get filtersOpen(): boolean {
        const { isMobile } = this.screenSizeStore;

        // if desktop and the side filters, it is always open and not toggleable
        if (!isMobile && !this.isFiltersTop) {
            return true;
        }

        return !this.filtersTopExpandable || this.isFiltersOpen;
    }

    @computed
    get filtersHeaderToggleProps() {
        const {
            isFiltersOpen,
            toggleExpandFilters,
            filtersTopExpandable,
        } = this;
        let defaultProps: FiltersProps = {
            id: 'FiltersHeaderToggle',
            'data-metric-id': 'filterHeader-top-toggle',
        };
        if (filtersTopExpandable) {
            defaultProps = {
                ...defaultProps,
                onClick: toggleExpandFilters,
                'aria-expanded': isFiltersOpen,
                'aria-controls': 'filters-top-expanded-content',
            };
        }
        return defaultProps;
    }

    @computed
    get filtersActive(): boolean {
        return this.filtersCount > 0 ? true : false;
    }

    @computed
    get shareableLinkOptions(): ShareableLink {
        return this.dataStore.config.shareableLink;
    }

    @computed
    get showShareableLink(): boolean {
        return this.shareableLinkOptions && this.shareableLinkOptions.show;
    }

    @computed
    get shareableLinkLabel(): string {
        return (
            (this.shareableLinkOptions && this.shareableLinkOptions.label) ||
            'Share'
        );
    }

    @computed
    get shareableLinkConfirmation(): string {
        return (
            (this.shareableLinkOptions &&
                this.shareableLinkOptions.confirmation) ||
            'Successfully copied to clipboard'
        );
    }

    @computed
    get shareableLinkFeedback(): FeedbackConfig {
        return this.shareableLinkOptions && this.shareableLinkOptions.feedback;
    }

    getFilterParamsForQueryString = (
        options: QueryStringOptions = {}
    ): { [key: string]: any } => {
        const params = getNonFilterParams(window.location.search);
        const {
            inputKeys = [],
            aliases = {},
            dataStoreQueries = [],
            queries = [],
            sort = false,
        } = options;
        if (inputKeys && inputKeys.length > 0) {
            const { appDataProps } = this.dataStore;
            for (const inputKey of inputKeys) {
                const value = appDataProps[inputKey];
                if (typeof value !== 'undefined') {
                    params[inputKey] = value;
                }
            }
        }

        this.dataStore.loadDataStoreQueries(params, dataStoreQueries);
        this.dataStore.loadQueries(params, queries);

        this.filters.forEach(filter => {
            const values = filter.values
                .filter(value =>
                    this.isChecked(
                        filter.property,
                        value.value,
                        filter.filterSearchKey
                    )
                )
                .map(v => encodeURIComponent(v.value));
            if (values.length > 0) {
                const filterValues = sort ? [...values].sort() : values;
                params[`_${filter.property}`] = filterValues.join('|');
            }
        });

        if (aliases.key && aliases.key.length > 0) {
            for (const keyAlias of aliases.key) {
                const { replacement, target } = keyAlias;
                params[replacement] = params[target];
                delete params[target];
            }
        }
        if (aliases.value && aliases.value.length > 0) {
            const valueAliasMap = new Map<any, any>();
            for (const valueAlias of aliases.value) {
                const { replacement, target } = valueAlias;
                valueAliasMap.set(target, replacement);
            }
            Object.keys(params).forEach(key => {
                const value = params[key];
                if (valueAliasMap.has(value)) {
                    params[key] = valueAliasMap.get(value);
                }
            });
        }
        return params;
    };

    getSelectedViewParamsForQueryString = (params: {
        [key: string]: string;
    }) => {
        const { config } = this.dataStore;
        const { selectedFundType, selectedModule, columnConfig } = config;

        if (selectedFundType && selectedFundType.fundType) {
            Object.assign(params, {
                categoryTab: encodeURIComponent(
                    selectedFundType.fundType.toLowerCase()
                ),
            });
        } else {
            console.warn(
                'Could not determine selected Fund Type for shareable link'
            );
        }
        if (selectedModule && selectedModule.id) {
            Object.assign(params, {
                tab: encodeURIComponent(selectedModule.id.toLowerCase()),
            });
        } else {
            console.warn(
                'Could not determine selected Module for shareable link'
            );
        }

        const groupColumn = {};
        columnConfig.forEach((column, idx) => {
            let groupColumnKey;
            if (column.type === 'dropdown') {
                // Due to the guid's being generated on load, the previous guid will not match to a new instance
                if (column.key && !isGUID(column.key)) {
                    groupColumnKey = `columnGroup_${encodeURIComponent(
                        column.key
                    )}`;
                    groupColumn[groupColumnKey] = this.getSelectedColumnValue(
                        column,
                        idx
                    );
                } else {
                    groupColumnKey = `columnGroup_${idx}`;
                    groupColumn[groupColumnKey] = this.getSelectedColumnValue(
                        column,
                        idx
                    );
                }
                Object.assign(params, groupColumn);
            }
        });
    };

    getSelectedColumnValue(column: ColumnConfig, idx: number) {
        const { columnStore } = this.dataStore;
        const { getSelectedGroup, selectedGroupMap } = columnStore;
        const selectedColumnValue = getSelectedGroup(column.key);
        if (selectedColumnValue.key) {
            return encodeURIComponent(selectedColumnValue.key);
        } else {
            return selectedGroupMap.get(idx);
        }
    }

    @computed
    get shareableLinkHref(): string {
        const { origin, pathname } = window.location;
        const params = this.getFilterParamsForQueryString();
        this.getSelectedViewParamsForQueryString(params);
        const searchTerms = Object.keys(params).map(key => {
            return `${key}=${params[key]}`;
        });
        const search = searchTerms.join('&');
        return `${origin}${pathname}?${search}`;
    }

    @action
    copyShareableLinkToClipboard = async () => {
        const hasGlobalFeedbackConfig =
            this.dataStore.config.globalConfig?.feedback !== undefined;

        // Backwards compatibility
        const useLegacyAlert =
            !hasGlobalFeedbackConfig && !this.shareableLinkFeedback;

        try {
            await navigator.clipboard.writeText(this.shareableLinkHref);

            if (useLegacyAlert) {
                this.showShareableLinkConfirm = true;
            } else {
                this.feedbackStore.addMessage({
                    text: this.shareableLinkConfirmation,
                    feedbackType: FeedbackType.success,
                    config: this.shareableLinkFeedback,
                    queryContext: {
                        response: {
                            url: this.shareableLinkHref,
                        },
                    },
                });
            }
            const analyticsContext: models.helpers.AnalyticsHelper.IAnalytics<models.helpers.AnalyticsHelper.IFundCenterContext> = {
                event: 'share_link_copied',
                eventType: 'userActioned',
                context: {
                    link: this.shareableLinkHref,
                },
            };
            this.dataStore.analyticsHelper.logEvent(analyticsContext);
        } catch (error) {
            console.error('Unable to copy document URL', error);
            if (!useLegacyAlert) {
                this.feedbackStore.addMessage({
                    text:
                        'Unable to copy document URL, please contact the administrator',
                    feedbackType: FeedbackType.error,
                    config: this.shareableLinkFeedback,
                });
            }
        }
    };

    @action
    closeShareableLinkConfirm = () => {
        this.showShareableLinkConfirm = false;
    };

    @computed
    get clearFiltersLabel(): string {
        let label = this.dataStore.config.clearFiltersLabel;
        if (utils.typeChecks.isNullOrUndefined(label)) {
            label = 'Clear filters';
        }
        return label;
    }

    @computed
    get clearIndividualFilters(): ClearIndividualFilters {
        return (
            this.dataStore.config.clearIndividualFilters || {
                enabled: false,
                align: 'left',
            }
        );
    }

    @action
    clearFilters = () => {
        this.dataStore.clearFilters();
        this.userFilters = {};
        this.setUserFilters();
    };

    @action
    clearFiltersOfType = (
        property: string,
        filterSearchKey: FilterSearchKeys
    ) => {
        const filterConfig = this.filtersByProperty[property];
        this.dataStore.clearFiltersOfType(property, filterSearchKey);
        if (this.userFilters[property]) {
            delete this.userFilters[property];
            this.setUserFilters();
        }
        if (filterConfig.id) {
            this.removeFiltersByParentId(filterConfig.id, filterSearchKey);
        }
    };

    @action
    toggleFilter = (
        property: string,
        value: string,
        filterSearchKey: FilterSearchKeys,
        checked: boolean,
        filter: FilterProps
    ) => {
        const shouldUpdateUserFilters = !!filter.saveValueAsUserPreference;
        if (filter.ui === 'dropdown') {
            this.toggleDropdownFilter(
                property,
                value,
                filterSearchKey,
                shouldUpdateUserFilters
            );
        } else {
            if (checked) {
                this.removeFilter(
                    property,
                    value,
                    filterSearchKey,
                    shouldUpdateUserFilters
                );
            } else {
                this.addFilter(
                    property,
                    value,
                    filterSearchKey,
                    shouldUpdateUserFilters
                );
            }
        }
        if (filter.id) {
            this.removeFiltersByParentId(filter.id, filterSearchKey);
        }
    };

    @action
    toggleDropdownFilter = (
        property: string,
        value: string,
        filterSearchKey: FilterSearchKeys,
        shouldUpdateUserFilters: boolean
    ) => {
        const updatedSearch = toJS(this.dataStore[filterSearchKey]);
        let filterAlreadySet = false;
        // remove existing filter
        if (updatedSearch && updatedSearch.length > 0) {
            for (const oldRecord of updatedSearch) {
                const { values: oldValues } = oldRecord;
                for (const oldValue of oldValues) {
                    if (oldValue !== value) {
                        this.removeFilter(
                            property,
                            oldValue,
                            filterSearchKey,
                            shouldUpdateUserFilters
                        );
                    } else {
                        filterAlreadySet = true;
                    }
                }
            }
        }
        // add new filter
        if (!filterAlreadySet) {
            this.addFilter(
                property,
                value,
                filterSearchKey,
                shouldUpdateUserFilters
            );
        }
    };

    @action
    addFilter = (
        property: string,
        value: string,
        filterSearchKey: FilterSearchKeys,
        shouldUpdateUserFilters: boolean
    ) => {
        const updatedSearch = toJS(this.dataStore[filterSearchKey]);
        const existingPropertyIndex = updatedSearch.findIndex(
            search => search.property === property
        );

        if (existingPropertyIndex >= 0) {
            updatedSearch[existingPropertyIndex].values.push(value);
            this.dataStore[filterSearchKey] = updatedSearch;
        } else {
            updatedSearch.push({
                property,
                values: [value],
                matchtype: 'MATCH',
            });
            this.dataStore[filterSearchKey] = updatedSearch;
        }
        this.logDataLayer('filter_selected', {
            propertyCode: property,
            selectedValue: value,
        });
        if (shouldUpdateUserFilters) {
            if (
                utils.typeChecks.isNullOrUndefined(this.userFilters[property])
            ) {
                this.userFilters[property] = [];
            }
            this.userFilters[property].push(value);
            this.setUserFilters();
        }
    };

    @action
    removeFilter = (
        property: string,
        value: string,
        filterSearchKey: FilterSearchKeys,
        shouldUpdateUserFilters: boolean
    ) => {
        const updatedSearch = toJS(this.dataStore[filterSearchKey]);
        const existingPropertyIndex = updatedSearch.findIndex(
            search => search.property === property
        );

        if (existingPropertyIndex >= 0) {
            updatedSearch[existingPropertyIndex].values = updatedSearch[
                existingPropertyIndex
            ].values.filter(existingValue => existingValue !== value);

            if (updatedSearch[existingPropertyIndex].values.length > 0) {
                this.dataStore[filterSearchKey] = updatedSearch;
            } else {
                this.dataStore[filterSearchKey] = updatedSearch.filter(
                    search => search.property !== property
                );
            }
        } else {
            this.dataStore[filterSearchKey] = updatedSearch.filter(
                search => search.property !== property
            );
        }
        if (shouldUpdateUserFilters) {
            if (
                !utils.typeChecks.isNullOrEmpty(this.userFilters[property]) &&
                this.userFilters[property].includes(value)
            ) {
                this.userFilters[property] = this.userFilters[property].filter(
                    existingValue => existingValue !== value
                );
                // Remove the key if there is no other values
                if (
                    utils.typeChecks.isNullOrEmpty(
                        toJS(this.userFilters[property])
                    )
                ) {
                    delete this.userFilters[property];
                }
                this.setUserFilters();
            }
        }
    };

    @action
    removeFiltersByParentId = (
        parentId: string,
        filterSearchKey: FilterSearchKeys
    ): void => {
        // clear direct children of parent id
        const updatedSearch = toJS(this.dataStore[filterSearchKey]);
        this.dataStore[filterSearchKey] = updatedSearch.filter(filter => {
            const filterConfig = this.filtersByProperty[filter.property];
            return !filterConfig.parentId || filterConfig.parentId !== parentId;
        });
        // apply recursive logic to clear children of children
        const childFilters = updatedSearch.filter(filter => {
            const filterConfig = this.filtersByProperty[filter.property];
            if (filterConfig.saveValueAsUserPreference) {
                delete this.userFilters[filter.property];
                this.setUserFilters();
            }
            return filterConfig.parentId === parentId;
        });
        for (const filter of childFilters) {
            const filterConfig = this.filtersByProperty[filter.property];
            if (filterConfig.id) {
                this.removeFiltersByParentId(filterConfig.id, filterSearchKey);
            }
        }
    };

    filterValue = (property: string, filterSearchKey: FilterSearchKeys) => {
        const existingProperty = this.dataStore[filterSearchKey].find(
            search => search.property === property
        );

        if (existingProperty) {
            return (
                existingProperty.values &&
                existingProperty.values.length > 0 &&
                existingProperty.values[0]
            );
        }
        return '';
    };

    isChecked = (
        property: string,
        value: string,
        filterSearchKey: FilterSearchKeys
    ): boolean => {
        const existingProperty = this.dataStore[filterSearchKey].find(
            search => search.property === property
        );

        if (existingProperty) {
            return existingProperty.values.includes(value);
        }

        return false;
    };

    isActive = (property: string, value: string): boolean => {
        const { activeFilters } = this.dataStore;
        const filterValues = activeFilters[property];
        if (filterValues) {
            return (
                filterValues.filter(filterValue => filterValue.value === value)
                    .length > 0
            );
        }

        return true;
    };

    @action
    logDataLayer(event: string, filterContext: object) {
        const { entityType } = this.dataStore.config;
        const analyticsContext: models.helpers.AnalyticsHelper.IAnalytics<models.helpers.AnalyticsHelper.IFundCenterContext> = {
            event,
            eventType: 'userActioned',
            context: {
                relatedEntityType: entityType, // or FUND, ACCT, BMRK
                ...filterContext,
            },
        };
        this.dataStore.analyticsHelper.logEvent(analyticsContext);
    }

    // Filters documents based on document properties
    // Loop over document property searches and get the value of any matching document property,
    // then check if that value is included in the filter values
    documentPropertyFilter = document => {
        const { documentPropertySearch } = this.dataStore;
        for (const filter of documentPropertySearch) {
            const documentProperty = get(document, filter.property);
            if (documentProperty) {
                return filter.values.includes(documentProperty);
            }
        }
    };

    // Filters documents based on document meta
    // Loop over document filters and get the value for specified meta (property),
    // then return if that value is included in the filter values
    documentMetaOrInferredFilter = document => {
        let response = true;
        const {
            documentFilterSearch = [],
            documentFilterInferredSearch = [],
        } = this.dataStore;
        if (response) {
            response = this.checkDocumentMatchesFilterSearches(
                documentFilterSearch,
                (filter: any) => {
                    return get(document, `meta.${filter.property}.value`);
                }
            );
        }
        if (response) {
            response = this.checkDocumentMatchesFilterSearches(
                documentFilterInferredSearch,
                (filter: any) => {
                    return get(document, `inferred.${filter.property}`);
                }
            );
        }
        return response;
    };

    checkDocumentMatchesFilterSearches = (
        collection: Array<any>,
        getValue: (filter: any) => any
    ) => {
        let response = true;
        for (const filter of collection) {
            const value = getValue(filter);
            response = this.checkValue(value, filter);
            if (!response) {
                break;
            }
        }
        return response;
    };

    checkValue = (value: any, filter: any) => {
        if (!isNullOrUndefined(value)) {
            if (isArrayLike(value)) {
                return filter.values.some(v => value.includes(v));
            } else {
                return filter.values.includes(value);
            }
        }
        return true;
    };
}



// WEBPACK FOOTER //
// ./src/stores/FilterStore.ts